import { useEffect, useState } from "react";
import { socket } from "../../socket";
import LoginForm from "../LoginForm";
import RoomFull from "../RoomFull";
import styles from "./ConnectionManager.module.css";
import GameOver from "../GameOver";
import GameSelector from "../GameSelector";
import Waiting from "../Waiting";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { gameOver } from "../../features/game/gameSlice";
import {
  logout,
  removeRoom,
  removeRoomId,
} from "../../features/user/userSlice";
import { useTranslation } from "react-i18next";

function ConnectionManager() {
  const [isConnected, setIsConnected] = useState(socket.connected);
  const [isRoomFull, setIsRoomFull] = useState<boolean>(false);
  const [gameOverMsg, setGameOverMsg] = useState<string>("");

  const isLoggedIn = useAppSelector((state) => state.user.isLoggedIn);
  const hasRoomAssigned = useAppSelector((state) => state.user.hasRoomAssigned);
  const game = useAppSelector((state) => state.game.game);
  const theGameIsOver = useAppSelector((state) => state.game.isOver);

  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  useEffect(() => {
    function onConnect() {
      setIsConnected(true);
    }

    function onDisconnect() {
      setIsConnected(false);
      dispatch(removeRoom());
      dispatch(removeRoomId());
    }

    function onRoomFull() {
      setIsRoomFull(true);
      socket.disconnect();
    }

    function onGameOver(msg: string) {
      setGameOverMsg(msg ? msg : "");
      dispatch(gameOver());
      dispatch(logout());
      socket.disconnect();
    }

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("room full", onRoomFull);
    socket.on("game over", onGameOver);

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("room full", onRoomFull);
      socket.off("game over", onGameOver);
    };
  }, [dispatch]);

  function disconnect() {
    setGameOverMsg(String(t("ConnectionManager.leaveMessage")));
    dispatch(gameOver());
    dispatch(logout());
    socket.disconnect();
  }

  if (theGameIsOver) {
    return <GameOver msg={gameOverMsg} />;
  }

  if (!isLoggedIn) {
    if (!hasRoomAssigned) {
      return <GameSelector />;
    }

    if (isRoomFull) {
      return <RoomFull />;
    }

    return <LoginForm />;
  } else {
    if (!isConnected) {
      return (
        <div className="error">
          <h1>{t("ConnectionManager.errorTitle")}</h1>
          <p>{t("ConnectionManager.errorMessage")}</p>
        </div>
      );
    }

    if (game.state === "started") {
      return (
        <button
          className={styles.close}
          onClick={disconnect}
          disabled={!isConnected}
        >
          {t("ConnectionManager.leaveButton")}
        </button>
      );
    }

    return (
      <Waiting
        activeNumberOfPlayers={game.players.length}
        totalNumberOfPlayers={game.totalPlayers}
      />
    );
  }
}

export default ConnectionManager;
